// src/area/InstitucionesArea/ImportAccreditationsModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import {
    FiCalendar,
    FiEdit2,
    FiFileText,
    FiSearch,
    FiTrash2,
    FiUpload,
    FiUser,
    FiUserPlus,
    FiUsers,
    FiX,
} from "react-icons/fi";
import API_BASE_URL from "../../config/config.js";
import SisdeuImportPanel from "./SisdeuImportPanel.jsx";
import { formatSisdeuName } from "../../utils/sisdeuName.js";

const TAB_IMPORT = "import";
const TAB_ACCREDITATIONS = "accreditations";

const emptyEditForm = {
    lastname: "",
    surname: "",
    firstname: "",
    docnumber: "",
    birthday: "",
    function_name: "",
};

const MANUAL_FUNCTION_OPTIONS = [
    "Deportista",
    "Delegado",
    "Entrenador",
    "Asistente",
    "Fisioterapista",
];

const emptyManualForm = {
    docnumber: "",
    firstname: "",
    lastname: "",
    surname: "",
    function_name: "Deportista",
};

const normalizeFunctionName = (value) =>
    String(value ?? "")
        .replace(/\s+/g, " ")
        .trim();

const ImportAccreditationsModal = ({
    open,
    onClose,
    institution,
    idevent,
    idsport,
}) => {
    const [activeTab, setActiveTab] = useState(TAB_IMPORT);

    const [rawText, setRawText] = useState("");
    const [isSubmittingImport, setIsSubmittingImport] = useState(false);
    const [serverErrors, setServerErrors] = useState([]);

    const [accreditations, setAccreditations] = useState([]);
    const [isLoadingAccreditations, setIsLoadingAccreditations] = useState(false);
    const [listError, setListError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState(emptyEditForm);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [isManualModalOpen, setIsManualModalOpen] = useState(false);
    const [manualForm, setManualForm] = useState(emptyManualForm);
    const [isSavingManual, setIsSavingManual] = useState(false);
    // FTR-0048: la importación SISDEU en curso bloquea el cierre del modal.
    const [isSisdeuBusy, setIsSisdeuBusy] = useState(false);
    // FTR-0052: con la integración SISDEU activa los nombres de la lista se muestran en Title Case (solo presentación).
    const [isSisdeuActive, setIsSisdeuActive] = useState(false);

    useEffect(() => {
        if (!open) {
            setActiveTab(TAB_IMPORT);
            setRawText("");
            setServerErrors([]);
            setAccreditations([]);
            setIsSubmittingImport(false);
            setIsLoadingAccreditations(false);
            setListError("");
            setSearchTerm("");
            setEditingId(null);
            setEditForm(emptyEditForm);
            setIsSavingEdit(false);
            setDeletingId(null);
            setIsManualModalOpen(false);
            setManualForm(emptyManualForm);
            setIsSavingManual(false);
            setIsSisdeuBusy(false);
            setIsSisdeuActive(false);
        }
    }, [open]);

    const lineCount = useMemo(() => {
        return rawText
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean).length;
    }, [rawText]);

    const filteredAccreditations = useMemo(() => {
        const query = String(searchTerm ?? "").trim().toLowerCase();

        if (!query) return accreditations;

        return accreditations.filter((item) => {
            const persona = String(item?.person?.persona || item?.persona || "").toLowerCase();
            const firstname = String(item?.person?.firstname || "").toLowerCase();
            const lastname = String(item?.person?.lastname || "").toLowerCase();
            const surname = String(item?.person?.surname || "").toLowerCase();
            const docnumber = String(item?.person?.docnumber || "").toLowerCase();
            const birthday = String(item?.person?.birthday || "").toLowerCase();
            const functionName = String(item?.function_name || "").toLowerCase();
            const jersey = String(item?.nro_camiseta || "").toLowerCase();

            return (
                persona.includes(query) ||
                firstname.includes(query) ||
                lastname.includes(query) ||
                surname.includes(query) ||
                docnumber.includes(query) ||
                birthday.includes(query) ||
                functionName.includes(query) ||
                jersey.includes(query)
            );
        });
    }, [accreditations, searchTerm]);

    const handleClose = () => {
        if (isSubmittingImport || isSavingEdit || deletingId || isSavingManual || isSisdeuBusy) return;
        onClose?.();
    };

    const openManualModal = () => {
        if (!institution || isSavingManual) return;
        setManualForm(emptyManualForm);
        setIsManualModalOpen(true);
    };

    const closeManualModal = () => {
        if (isSavingManual) return;
        setIsManualModalOpen(false);
        setManualForm(emptyManualForm);
    };

    const handleChangeManualForm = (field, value) => {
        setManualForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const loadAccreditations = useCallback(async () => {
        if (!institution?.idinstitution || !idevent || !idsport) return;

        try {
            setIsLoadingAccreditations(true);
            setListError("");

            const { data } = await axios.post(
                `${API_BASE_URL}/accreditations/by-institution-detailed`,
                {
                    idevent: Number(idevent),
                    idsport: Number(idsport),
                    idinstitution: Number(institution.idinstitution),
                }
            );

            setAccreditations(Array.isArray(data?.items) ? data.items : []);
        } catch (error) {
            console.error("Error loading accreditations by institution:", error);
            setListError(
                error?.response?.data?.message ||
                "No se pudo cargar los acreditados de la institución."
            );
            setAccreditations([]);
        } finally {
            setIsLoadingAccreditations(false);
        }
    }, [institution?.idinstitution, idevent, idsport]);

    useEffect(() => {
        if (open && activeTab === TAB_ACCREDITATIONS) {
            loadAccreditations();
        }
    }, [open, activeTab, loadAccreditations]);

    // FTR-0048: tras importar desde SISDEU se muestra la lista actualizada de acreditados.
    const handleSisdeuImported = () => {
        if (activeTab === TAB_ACCREDITATIONS) {
            loadAccreditations();
        } else {
            setActiveTab(TAB_ACCREDITATIONS);
        }
    };

    const handleImport = async () => {
        if (!institution?.idinstitution || !rawText.trim() || isSubmittingImport) return;

        try {
            setIsSubmittingImport(true);
            setServerErrors([]);

            const { data } = await axios.post(
                `${API_BASE_URL}/accreditations/import-from-textarea`,
                {
                    idevent: Number(idevent),
                    idsport: Number(idsport),
                    idinstitution: Number(institution.idinstitution),
                    rawText,
                }
            );

            const summary = data?.summary || {};
            const importErrors = Array.isArray(data?.errors) ? data.errors : [];

            alert(
                [
                    `Importación completada para ${institution?.name || institution?.abrev || "la institución"}.`,
                    ``,
                    `Filas válidas: ${summary.validRows ?? 0}`,
                    `Filas inválidas: ${summary.invalidRows ?? 0}`,
                    `Person creados: ${summary.createdPersons ?? 0}`,
                    `Person actualizados: ${summary.updatedPersons ?? 0}`,
                    `Person reutilizados: ${summary.reusedPersons ?? 0}`,
                    `Accreditations creados: ${summary.createdAccreditations ?? 0}`,
                    `Accreditations ya existentes: ${summary.skippedExistingAccreditations ?? 0}`,
                    importErrors.length ? `` : null,
                    importErrors.length
                        ? `Se omitieron ${importErrors.length} fila(s) inválida(s).`
                        : null,
                ]
                    .filter(Boolean)
                    .join("\n")
            );

            setRawText("");
            setActiveTab(TAB_ACCREDITATIONS);
            await loadAccreditations();
        } catch (error) {
            console.error("Error importing accreditations:", error);

            const responseData = error?.response?.data;
            const backendErrors = Array.isArray(responseData?.errors)
                ? responseData.errors
                : [];

            setServerErrors(backendErrors);

            alert(
                responseData?.message ||
                "No se pudo importar los acreditados desde el textarea."
            );
        } finally {
            setIsSubmittingImport(false);
        }
    };

    const handleCreateManualAccreditation = async () => {
        if (!institution?.idinstitution || isSavingManual) return;

        const docnumber = String(manualForm.docnumber ?? "").replace(/\s+/g, "").trim();
        const firstname = String(manualForm.firstname ?? "").replace(/\s+/g, " ").trim();
        const lastname = String(manualForm.lastname ?? "").replace(/\s+/g, " ").trim();
        const surname = String(manualForm.surname ?? "").replace(/\s+/g, " ").trim();
        const functionName = normalizeFunctionName(manualForm.function_name) || "Deportista";

        if (!docnumber) {
            alert("Documento es obligatorio.");
            return;
        }

        if (!firstname) {
            alert("Nombres es obligatorio.");
            return;
        }

        if (!MANUAL_FUNCTION_OPTIONS.includes(functionName)) {
            alert("Selecciona una función válida.");
            return;
        }

        try {
            setIsSavingManual(true);

            const { data } = await axios.post(
                `${API_BASE_URL}/accreditations/manual`,
                {
                    idevent: Number(idevent),
                    idsport: Number(idsport),
                    idinstitution: Number(institution.idinstitution),
                    docnumber,
                    firstname,
                    lastname,
                    surname,
                    function_name: functionName,
                }
            );

            setIsManualModalOpen(false);
            setManualForm(emptyManualForm);
            setActiveTab(TAB_ACCREDITATIONS);
            await loadAccreditations();

            alert(data?.message || "Acreditado añadido correctamente.");
        } catch (error) {
            console.error("Error creating manual accreditation:", error);
            alert(
                error?.response?.data?.message ||
                "No se pudo añadir el acreditado manualmente."
            );
        } finally {
            setIsSavingManual(false);
        }
    };

    const startEdit = (item) => {
        setEditingId(item?.idacreditation ?? null);
        setEditForm({
            lastname: item?.person?.lastname || "",
            surname: item?.person?.surname || "",
            firstname: item?.person?.firstname || "",
            docnumber: item?.person?.docnumber || "",
            birthday: item?.person?.birthday || "",
            function_name: item?.function_name || "",
        });
    };

    const cancelEdit = () => {
        if (isSavingEdit) return;
        setEditingId(null);
        setEditForm(emptyEditForm);
    };

    const handleChangeEditForm = (field, value) => {
        setEditForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSaveEdit = async (idacreditation) => {
        if (!idacreditation || isSavingEdit) return;

        const nextFunctionName = normalizeFunctionName(editForm.function_name);

        if (!nextFunctionName) {
            alert("Función es obligatoria.");
            return;
        }

        try {
            setIsSavingEdit(true);

            await axios.patch(
                `${API_BASE_URL}/accreditations/${idacreditation}/person`,
                {
                    lastname: editForm.lastname,
                    surname: editForm.surname,
                    firstname: editForm.firstname,
                    docnumber: editForm.docnumber,
                    birthday: editForm.birthday,
                }
            );

            const { data } = await axios.patch(
                `${API_BASE_URL}/accreditations/${idacreditation}/function`,
                {
                    function_name: nextFunctionName,
                }
            );

            const updatedItem = data?.item;

            setAccreditations((prev) =>
                prev.map((item) =>
                    item.idacreditation === idacreditation ? updatedItem : item
                )
            );

            setEditingId(null);
            setEditForm(emptyEditForm);

            alert(data?.message || "Acreditado actualizado correctamente.");
        } catch (error) {
            console.error("Error updating accreditation:", error);

            try {
                await loadAccreditations();
            } catch (reloadError) {
                console.error("Error reloading accreditations after update failure:", reloadError);
            }

            alert(
                error?.response?.data?.message ||
                "No se pudo actualizar el acreditado."
            );
        } finally {
            setIsSavingEdit(false);
        }
    };

    const handleDelete = async (item) => {
        const idacreditation = Number(item?.idacreditation ?? 0) || 0;
        if (!idacreditation || deletingId) return;

        const confirmed = window.confirm(
            `¿Seguro que deseas eliminar la accreditation de ${item?.person?.persona || item?.persona || "este acreditado"}?\n\n`
        );

        if (!confirmed) return;

        try {
            setDeletingId(idacreditation);

            const { data } = await axios.delete(
                `${API_BASE_URL}/accreditations/${idacreditation}`
            );

            setAccreditations((prev) =>
                prev.filter((row) => row.idacreditation !== idacreditation)
            );

            if (editingId === idacreditation) {
                setEditingId(null);
                setEditForm(emptyEditForm);
            }

            alert(data?.message || "Acreditado eliminado correctamente.");
        } catch (error) {
            console.error("Error deleting accreditation:", error);
            alert(
                error?.response?.data?.message || "No se pudo eliminar el acreditado."
            );
        } finally {
            setDeletingId(null);
        }
    };

    const renderImportTab = () => (
        <div className="space-y-4 px-5 py-5 sm:px-6">
            <div className="rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-slate-50 px-4 py-3">
                <p className="text-sm font-semibold text-slate-800">
                    Formato esperado al pegar
                </p>
                <p className="mt-1 text-sm text-slate-600">
                    Puedes pegar directamente desde Excel. Se usan las columnas{" "}
                    <strong>Paterno</strong>, <strong>Nombres</strong>, <strong>Documento</strong>,{" "}
                    opcionalmente <strong>Materno</strong>, <strong>Nacimiento</strong> en formato{" "}
                    <strong>dd/mm/yyyy</strong>, <strong>Función</strong>/<strong>Funcion</strong> y{" "}
                    <strong>Camiseta</strong>. Si Función no viene o llega vacía, se guarda como{" "}
                    <strong>Deportista</strong>. Si Camiseta no viene o llega vacía, se guarda como{" "}
                    <strong>-</strong>.
                </p>
            </div>

            <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                    <label className="text-sm font-semibold text-slate-700">
                        Texto pegado desde Excel
                    </label>
                    <span className="text-xs font-medium text-slate-500">
                        Líneas detectadas: {lineCount}
                    </span>
                </div>

                <textarea
                    value={rawText}
                    onChange={(e) => setRawText(e.target.value)}
                    placeholder={`Paterno\tMaterno\tNombres\tDocumento\tNacimiento\tFunción
CANALES\tROJAS\tMARÍA MARCELA\t75535093\t15/08/2008\tDeportista`}
                    className="h-[min(38dvh,320px)] w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
            </div>

            {serverErrors.length > 0 && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
                    <p className="text-sm font-semibold text-red-700">
                        Filas con error
                    </p>
                    <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-sm text-red-600">
                        {serverErrors.slice(0, 12).map((item, index) => (
                            <li key={`${item?.row}-${index}`}>
                                Fila {item?.row}: {item?.reason}
                            </li>
                        ))}
                        {serverErrors.length > 12 && (
                            <li>Y {serverErrors.length - 12} error(es) más…</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );

    const renderReadonlyRow = (label, value, icon = null) => (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {icon}
                <span>{label}</span>
            </div>
            <div className="mt-1 text-sm text-slate-800">{value || "—"}</div>
        </div>
    );

    const renderEditInput = (label, value, onChange, placeholder) => (
        <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                {label}
            </span>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
        </label>
    );

    const renderAccreditationsTab = () => (
        <div className="space-y-4 px-5 py-5 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <p className="text-sm font-semibold text-slate-800">
                        Acreditados existentes
                    </p>
                    <p className="text-sm text-slate-500">
                        Aquí puedes revisar, editar datos personales y la función, o eliminar la accreditation.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={loadAccreditations}
                    disabled={isLoadingAccreditations}
                    className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {isLoadingAccreditations ? "Recargando..." : "Recargar"}
                </button>
            </div>

            <div className="relative">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar por nombre, documento, nacimiento, función o camiseta"
                    className="w-full rounded-2xl border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                Total cargados: <strong>{accreditations.length}</strong>
                {searchTerm.trim() ? (
                    <>
                        {" "}
                        · Mostrando: <strong>{filteredAccreditations.length}</strong>
                    </>
                ) : null}
            </div>

            {listError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {listError}
                </div>
            ) : null}

            {isLoadingAccreditations ? (
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500">
                    Cargando acreditados...
                </div>
            ) : filteredAccreditations.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center text-sm text-slate-500">
                    No se encontraron acreditados para esta institución.
                </div>
            ) : (
                <div className="max-h-[420px] space-y-3 overflow-y-auto pr-1">
                    {filteredAccreditations.map((item) => {
                        const isEditing = editingId === item.idacreditation;
                        const isDeleting = deletingId === item.idacreditation;

                        return (
                            <div
                                key={item.idacreditation}
                                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                            >
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h3 className="text-base font-bold text-slate-900">
                                                {isSisdeuActive
                                                    ? formatSisdeuName(item?.person?.persona || item?.persona) || "Sin nombre"
                                                    : item?.person?.persona || item?.persona || "Sin nombre"}
                                            </h3>

                                            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700">
                                                {item?.function_name || "Sin función"}
                                            </span>

                                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                                Camiseta: {item?.nro_camiseta || "-"}
                                            </span>

                                            {/* FTR-0048: estado SISDEU real de lo importado con "Todos" (no aprobado). */}
                                            {item?.state && item.state !== "Aprobado" ? (
                                                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                                                    {item.state}
                                                </span>
                                            ) : null}
                                        </div>

                                        <p className="mt-1 text-xs text-slate-500">
                                            ID Accreditation: {item?.idacreditation}
                                        </p>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2">
                                        {!isEditing ? (
                                            <button
                                                type="button"
                                                onClick={() => startEdit(item)}
                                                disabled={Boolean(deletingId) || isLoadingAccreditations}
                                                className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <FiEdit2 className="h-4 w-4" />
                                                Editar
                                            </button>
                                        ) : null}

                                        <button
                                            type="button"
                                            onClick={() => handleDelete(item)}
                                            disabled={isDeleting || isSavingEdit}
                                            className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <FiTrash2 className="h-4 w-4" />
                                            {isDeleting ? "Eliminando..." : "Eliminar"}
                                        </button>
                                    </div>
                                </div>

                                {!isEditing ? (
                                    <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                        {renderReadonlyRow(
                                            "Documento",
                                            item?.person?.docnumber,
                                            <FiFileText className="h-3.5 w-3.5" />
                                        )}
                                        {renderReadonlyRow(
                                            "Nacimiento",
                                            item?.person?.birthday,
                                            <FiCalendar className="h-3.5 w-3.5" />
                                        )}
                                        {renderReadonlyRow(
                                            "Función",
                                            item?.function_name,
                                            <FiUsers className="h-3.5 w-3.5" />
                                        )}
                                        {renderReadonlyRow(
                                            "Paterno",
                                            item?.person?.lastname,
                                            <FiUser className="h-3.5 w-3.5" />
                                        )}
                                        {renderReadonlyRow(
                                            "Materno",
                                            item?.person?.surname,
                                            <FiUser className="h-3.5 w-3.5" />
                                        )}
                                    </div>
                                ) : (
                                    <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/40 p-4">
                                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                                            {renderEditInput(
                                                "Paterno",
                                                editForm.lastname,
                                                (value) => handleChangeEditForm("lastname", value),
                                                "Apellido paterno"
                                            )}
                                            {renderEditInput(
                                                "Materno",
                                                editForm.surname,
                                                (value) => handleChangeEditForm("surname", value),
                                                "Apellido materno"
                                            )}
                                            {renderEditInput(
                                                "Nombres",
                                                editForm.firstname,
                                                (value) => handleChangeEditForm("firstname", value),
                                                "Nombres"
                                            )}
                                            {renderEditInput(
                                                "Documento",
                                                editForm.docnumber,
                                                (value) => handleChangeEditForm("docnumber", value),
                                                "Documento"
                                            )}
                                            {renderEditInput(
                                                "Nacimiento",
                                                editForm.birthday,
                                                (value) => handleChangeEditForm("birthday", value),
                                                "dd/mm/yyyy"
                                            )}
                                            {renderEditInput(
                                                "Función",
                                                editForm.function_name,
                                                (value) => handleChangeEditForm("function_name", value),
                                                "Deportista, Asistente, Entrenador..."
                                            )}
                                        </div>

                                        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                            <button
                                                type="button"
                                                onClick={cancelEdit}
                                                disabled={isSavingEdit}
                                                className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                Cancelar
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => handleSaveEdit(item.idacreditation)}
                                                disabled={isSavingEdit}
                                                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                                            >
                                                {isSavingEdit ? "Guardando..." : "Guardar cambios"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const renderManualCreateModal = () => (
        <Modal open={isManualModalOpen} onClose={closeManualModal}>
            <ModalDialog
                layout="center"
                sx={{
                    p: 0,
                    border: "none",
                    background: "transparent",
                    boxShadow: "none",
                    width: "min(620px, calc(100vw - 24px))",
                    maxWidth: "620px",
                }}
            >
                <div className="overflow-hidden rounded-3xl bg-white shadow-2xl">
                    <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h3 className="text-lg font-bold text-slate-900">
                                    Añadir acreditado
                                </h3>
                                <p className="mt-1 text-sm text-slate-500">
                                    Se añadirá a {institution?.name || institution?.abrev || "la institución"}.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={closeManualModal}
                                disabled={isSavingManual}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <FiX className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    <div className="space-y-4 px-5 py-5 sm:px-6">
                        <div className="grid gap-3 md:grid-cols-2">
                            {renderEditInput(
                                "Nro documento",
                                manualForm.docnumber,
                                (value) => handleChangeManualForm("docnumber", value),
                                "DNI / CE / Documento"
                            )}
                            {renderEditInput(
                                "Nombres",
                                manualForm.firstname,
                                (value) => handleChangeManualForm("firstname", value),
                                "Nombres"
                            )}
                            {renderEditInput(
                                "Apellido paterno",
                                manualForm.lastname,
                                (value) => handleChangeManualForm("lastname", value),
                                "Apellido paterno"
                            )}
                            {renderEditInput(
                                "Apellido materno",
                                manualForm.surname,
                                (value) => handleChangeManualForm("surname", value),
                                "Apellido materno"
                            )}

                            <label className="block md:col-span-2">
                                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    Función
                                </span>
                                <select
                                    value={manualForm.function_name}
                                    onChange={(e) =>
                                        handleChangeManualForm("function_name", e.target.value)
                                    }
                                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                >
                                    {MANUAL_FUNCTION_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                            Si el documento ya existe, se reutiliza la persona. Si ya está acreditada en esta institución/evento/deporte, el sistema bloqueará el duplicado.
                        </div>
                    </div>

                    <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-end sm:px-6">
                        <button
                            type="button"
                            onClick={closeManualModal}
                            disabled={isSavingManual}
                            className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Cancelar
                        </button>

                        <button
                            type="button"
                            onClick={handleCreateManualAccreditation}
                            disabled={
                                isSavingManual ||
                                !manualForm.docnumber.trim() ||
                                !manualForm.firstname.trim() ||
                                !manualForm.function_name
                            }
                            className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
                        >
                            {isSavingManual ? "Añadiendo..." : "Añadir acreditado"}
                        </button>
                    </div>
                </div>
            </ModalDialog>
        </Modal>
    );

    return (
        <>
            <Modal open={open} onClose={handleClose}>
                <ModalDialog
                    layout="center"
                    sx={{
                        p: 0,
                        border: "none",
                        background: "transparent",
                        boxShadow: "none",
                        width: "min(1100px, calc(100vw - 24px))",
                        maxWidth: "1100px",
                        maxHeight: "calc(100dvh - 24px)",
                    }}
                >
                    <div className="flex max-h-[calc(100dvh-24px)] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
                        <div className="shrink-0 border-b border-slate-200 px-5 py-4 sm:px-6">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900 sm:text-xl">
                                        Gestionar acreditados de{" "}
                                        <span className="text-blue-600">
                                            {institution?.name || institution?.abrev || "institución"}
                                        </span>
                                    </h2>
                                    <p className="mt-1 text-sm text-slate-500">
                                        Usa el tab de agregar para importar desde Excel y el tab de acreditados para revisar, editar o eliminar.
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleClose}
                                    disabled={isSubmittingImport || isSavingEdit || Boolean(deletingId) || isSavingManual || isSisdeuBusy}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <FiX className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => setActiveTab(TAB_IMPORT)}
                                    className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${activeTab === TAB_IMPORT
                                        ? "bg-blue-600 text-white shadow"
                                        : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                                        }`}
                                >
                                    <FiUpload className="h-4 w-4" />
                                    Agregar
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setActiveTab(TAB_ACCREDITATIONS)}
                                    className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${activeTab === TAB_ACCREDITATIONS
                                        ? "bg-blue-600 text-white shadow"
                                        : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                                        }`}
                                >
                                    <FiUsers className="h-4 w-4" />
                                    Acreditados
                                </button>

                                <button
                                    type="button"
                                    onClick={openManualModal}
                                    disabled={!institution || isSavingManual}
                                    className="inline-flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <FiUserPlus className="h-4 w-4" />
                                    Añadir acreditado
                                </button>

                                <SisdeuImportPanel
                                    idevent={idevent}
                                    idsport={idsport}
                                    institution={institution}
                                    disabled={isSubmittingImport || isSavingEdit || Boolean(deletingId) || isSavingManual}
                                    onBusyChange={setIsSisdeuBusy}
                                    onIntegrationActiveChange={setIsSisdeuActive}
                                    onImported={handleSisdeuImported}
                                />
                            </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto">
                            {activeTab === TAB_IMPORT ? renderImportTab() : renderAccreditationsTab()}
                        </div>

                        <div className="shrink-0 flex flex-col-reverse gap-3 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-end sm:px-6">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={isSubmittingImport || isSavingEdit || Boolean(deletingId) || isSavingManual || isSisdeuBusy}
                                className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Cerrar
                            </button>

                            {activeTab === TAB_IMPORT ? (
                                <button
                                    type="button"
                                    onClick={handleImport}
                                    disabled={isSubmittingImport || !rawText.trim() || !institution}
                                    className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                                >
                                    {isSubmittingImport ? "Importando..." : "Importar acreditados"}
                                </button>
                            ) : null}
                        </div>
                    </div>
                </ModalDialog>
            </Modal>
            {renderManualCreateModal()}
        </>
    );
};

export default ImportAccreditationsModal;